// src/utils/referralCapture.ts
// Invite links are https://playmoonrock.com/?ref=CODE. Any URL with ?ref=
// stores the code in localStorage "moonrock-ref" (captured once at startup in
// index.tsx), and sign-up sends it as `ref`. A random device id (localStorage
// "moonrock-device") goes with sign-up and login so the server can tell a
// second account on the inviter's own device.

export const REF_KEY = "moonrock-ref";
export const DEVICE_KEY = "moonrock-device";

/** Upper case, letters and digits only, 4 to 12 long; anything else is null. */
export function normalizeRef(raw: string | null | undefined): string | null {
  const s = String(raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  return /^[A-Z0-9]{4,12}$/.test(s) ? s : null;
}

/** Reads ?ref= from a search string (defaults to the current URL) and stores it. Returns the code. */
export function captureRef(search: string = typeof window !== "undefined" ? window.location.search : ""): string | null {
  let code: string | null = null;
  try {
    code = normalizeRef(new URLSearchParams(search).get("ref"));
  } catch {
    code = null;
  }
  if (!code) return null;
  try {
    localStorage.setItem(REF_KEY, code);
  } catch {
    /* storage blocked: the code is still returned */
  }
  return code;
}

export function getStoredRef(): string | null {
  try {
    return normalizeRef(localStorage.getItem(REF_KEY));
  } catch {
    return null;
  }
}

export function clearStoredRef(): void {
  try {
    localStorage.removeItem(REF_KEY);
  } catch {
    /* ignore */
  }
}

function randomId(): string {
  const c = (typeof window !== "undefined" ? window.crypto : undefined) as Crypto | undefined;
  if (c?.getRandomValues) {
    const a = new Uint8Array(16);
    c.getRandomValues(a);
    return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

/** A stable random id for this browser (not personal data). */
export function deviceId(): string {
  try {
    const have = localStorage.getItem(DEVICE_KEY);
    if (have && /^[A-Za-z0-9_-]{8,80}$/.test(have)) return have;
    const id = randomId();
    localStorage.setItem(DEVICE_KEY, id);
    return id;
  } catch {
    return randomId();
  }
}
